import { useCallback, useEffect, useState } from 'react';
import type { Sessao } from '../../auth';
import { dataHoraCurta, podeFazer } from '../../acesso';
import type { Eu, Falha } from '../../acesso';
import { SemAcesso } from '../../design/Estados';
import { Icone } from '../../design/Icone';
import { useValoresOcultos, useVeCusto } from '../../design/sigilo';
import {
  incluirNoParadao,
  lerDetalheDoParadao,
  lerParadao,
  textoDaVenda,
  tirarDoParadao,
} from '../../paradao';
import type { CatalogoDoParadao, DetalheDoParadao, ItemDoParadao } from '../../paradao';
import { listarProdutos, reais } from '../../produtos';
import type { ProdutoComercial } from '../../produtos';
import { ErroDaLista, Esqueleto, mensagemDeFalha, useEscape } from '../equipe/Equipe';
import type { LancadosNoPeriodo } from '../../lancados-no-periodo';
import { Selo } from '../prevencidos/PreVencidos';
import '../equipe/acesso.css';
import '../produtos/produtos.css';
import '../prevencidos/prevencidos.css';
import './paradao.css';

/**
 * TELA 27 — PARADÃO. "'Sem venda há 126 dias' é um fato, não um veredito."
 *
 * A tela mostra o fato e deixa a causa para quem conhece a loja. A última venda vem da BASE DE
 * VENDAS (calculada no servidor), nunca do campo de última saída do cadastro. Quatro estados:
 * sem venda há N dias · nunca vendido · última venda indisponível. Com e sem estoque separados.
 * Nenhuma ação altera preço sozinha; nenhuma campanha nasce por estar no Paradão.
 */

type Estado =
  | { readonly fase: 'carregando' }
  | { readonly fase: 'erro'; readonly falha: Falha }
  | { readonly fase: 'pronto'; readonly dados: CatalogoDoParadao };

const CHIPS: readonly (readonly [string, string] | 'divisor')[] = [
  ['com_estoque', 'Com estoque'],
  ['sem_estoque', 'Sem estoque'],
  'divisor',
  ['90', '+90 dias'],
  ['180', '+180 dias'],
  ['365', '+365 dias'],
  ['nunca', 'Nunca vendidos'],
  'divisor',
  ['pre_vencido', 'Também pré-vencido'],
  ['todos', 'Todos'],
];

export function Paradao({
  sessao,
  eu,
  lancados,
}: {
  sessao: Sessao;
  eu: Eu | null;
  /** O filtro de período do módulo Paradão: quanto de cada um foi lançado nele. */
  lancados?: LancadosNoPeriodo | undefined;
}) {
  const veCusto = useVeCusto();
  const [filtro, setFiltro] = useState('com_estoque');
  const [busca, setBusca] = useState('');
  const [buscaAplicada, setBuscaAplicada] = useState('');
  const [pagina, setPagina] = useState(1);
  const [estado, setEstado] = useState<Estado>({ fase: 'carregando' });
  const [aberto, setAberto] = useState<string | null>(null);
  const [incluir, setIncluir] = useState(false);
  const configura = podeFazer(eu, 'produtos.configurar');

  useEffect(() => {
    const t = window.setTimeout(() => {
      setBuscaAplicada(busca);
      setPagina(1);
    }, 300);
    return () => window.clearTimeout(t);
  }, [busca]);

  const carregar = useCallback(async () => {
    setEstado((e) => (e.fase === 'pronto' ? e : { fase: 'carregando' }));
    const r = await lerParadao({
      filtro,
      busca: buscaAplicada,
      pagina,
      lancados: lancados ?? null,
    });
    setEstado(r.ok ? { fase: 'pronto', dados: r.dados } : { fase: 'erro', falha: r.falha });
  }, [filtro, buscaAplicada, pagina, lancados?.de, lancados?.ate, lancados?.so]);
  // Trocou o período: volta para a primeira página.
  useEffect(() => setPagina(1), [lancados?.de, lancados?.ate, lancados?.so]);
  useEffect(() => {
    void carregar();
  }, [carregar]);

  if (estado.fase === 'erro' && estado.falha.tipo === 'sem-acesso')
    return <SemAcesso area="Paradão" />;
  const dados = estado.fase === 'pronto' ? estado.dados : null;
  const r = dados?.resumo;

  return (
    <div className="ac ac-cheia pr">
      <div className="ac-cabeca">
        <div>
          <h1 className="ac-titulo">Produtos parados</h1>
          <div className="ac-subtitulo">
            Última venda calculada sobre a base de vendas
            {dados?.fonte.importadoEm
              ? ` · vendas até ${dataHoraCurta(dados.fonte.importadoEm)}`
              : ''}{' '}
            · faixas de dias definidas na configuração
          </div>
        </div>
        <div className="ac-acoes">
          <label className="ac-busca pv-busca">
            <Icone nome="search" tamanho={14} traco={1.6} />
            <input
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              placeholder="Produto, EAN, código ou fabricante"
              aria-label="Buscar no Paradão"
            />
          </label>
          {configura && (
            <a
              className="ac-botao"
              href="/api/lotes/exportar.csv?programa=paradao"
              download
              title="Planilha dos lotes com saldo e os dias parado (abre no Excel)"
            >
              <Icone nome="down" tamanho={14} traco={1.8} />
              Exportar estoque
            </a>
          )}
          {configura && (
            <button
              type="button"
              className="ac-botao ac-botao-primario"
              onClick={() => setIncluir(true)}
            >
              <Icone nome="plus" tamanho={14} traco={1.8} />
              Incluir produto
            </button>
          )}
        </div>
      </div>

      {dados && !dados.fonte.temBase && (
        <div className="pr-faixa-fonte" role="status">
          <Icone nome="warn" tamanho={14} traco={1.8} />A base de vendas ainda não tem vendas
          importadas: a última venda de cada produto fica <b>indisponível</b> — não "nunca vendido",
          não zero.
        </div>
      )}

      <div className="ac-kpis">
        <Kpi rotulo="COM ESTOQUE" valor={r?.comEstoque} sub="produtos parados" />
        <Kpi rotulo="SEM ESTOQUE" valor={r?.semEstoque} sub="registro histórico" tom="neutro" />
        <Kpi rotulo="+180 DIAS" valor={r?.mais180ComEstoque} sub="com estoque" tom="aviso" />
        <Kpi rotulo="NUNCA VENDIDOS" valor={r?.nuncaVendidos} sub="estado próprio" />
        {veCusto && (
          <div className="ac-kpi">
            <div className="ac-kpi-rotulo">CUSTO PARADO</div>
            <div className="ac-kpi-valor">
              {r ? (r.custoParado ? reaisInteiro(r.custoParado) : '—') : '—'}
            </div>
            <div className="pd-kpi-sub">
              {r && !r.custoParado ? 'indisponível: falta custo' : 'custo do servidor'}
            </div>
          </div>
        )}
      </div>

      <div className="ac-filtros">
        {CHIPS.map((c, i) =>
          c === 'divisor' ? (
            <span key={i} className="ac-divisor" />
          ) : (
            <button
              key={c[0]}
              type="button"
              aria-pressed={filtro === c[0]}
              className={`ac-chip${filtro === c[0] ? ' ac-chip-ativo' : ''}`}
              onClick={() => {
                setFiltro(c[0]);
                setPagina(1);
              }}
            >
              {c[1]}
            </button>
          ),
        )}
        <span className="ac-espaco" />
        <span className="ac-nota">faixas exibidas conforme a configuração vigente</span>
      </div>

      <div className="ac-cartao ac-cartao-tabela">
        {estado.fase === 'carregando' && <Esqueleto linhas={6} />}
        {estado.fase === 'erro' && (
          <div style={{ padding: 16 }}>
            <ErroDaLista
              titulo="Não foi possível carregar o Paradão"
              aoTentar={() => void carregar()}
            />
          </div>
        )}
        {dados && dados.itens.length === 0 && (
          <div className="ac-vazio">
            <div className="ac-vazio-titulo">
              {r && r.comEstoque + r.semEstoque === 0
                ? 'Nenhum produto no Paradão ainda'
                : 'Nenhum produto corresponde aos filtros'}
            </div>
            <div className="ac-vazio-texto">
              {r && r.comEstoque + r.semEstoque === 0
                ? 'A lista entra pela migração do 1.0 ou por "Incluir produto".'
                : 'Troque o filtro ou a busca.'}
            </div>
          </div>
        )}
        {dados && dados.itens.length > 0 && (
          <div className="ac-tabela-rolo">
            <table className="ac-tabela">
              <thead>
                <tr>
                  <th>PRODUTO</th>
                  <th>ÚLTIMA VENDA</th>
                  <th className="ac-direita">DIAS</th>
                  <th className="ac-direita ac-col-larga">30 D</th>
                  <th className="ac-direita ac-col-larga">90 D</th>
                  <th className="ac-direita">ESTOQUE</th>
                  {lancados && <th className="ac-direita">LANÇADOS NO PERÍODO</th>}
                  {veCusto && <th className="ac-direita">CUSTO</th>}
                  <th className="ac-col-larga">CURVA</th>
                  <th>TAGS</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {dados.itens.map((i) => (
                  <Linha
                    key={i.productId}
                    i={i}
                    aoAbrir={() => setAberto(i.productId)}
                    comLancados={!!lancados}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className="ac-rodape-tabela">
          <span>
            {dados && dados.total > 0
              ? `${String((dados.pagina - 1) * dados.tamanhoDaPagina + 1)}–${String((dados.pagina - 1) * dados.tamanhoDaPagina + dados.itens.length)} de ${String(dados.total)}`
              : ' '}
            {dados && dados.total > dados.tamanhoDaPagina && (
              <span className="pd-paginas">
                <button
                  type="button"
                  className="ac-link"
                  disabled={dados.pagina <= 1}
                  onClick={() => setPagina((n) => n - 1)}
                >
                  Anterior
                </button>
                <button
                  type="button"
                  className="ac-link"
                  disabled={dados.pagina * dados.tamanhoDaPagina >= dados.total}
                  onClick={() => setPagina((n) => n + 1)}
                >
                  Próxima
                </button>
              </span>
            )}
          </span>
          <span>
            Sem curva disponível é —, nunca "curva C" · sem estoque não sugere venda nem premiação
          </span>
        </div>
      </div>

      <div className="ac-cards">
        {dados?.itens.map((i) => (
          <CardDoParadao key={i.productId} i={i} aoAbrir={() => setAberto(i.productId)} />
        ))}
      </div>

      {aberto && (
        <DrawerDoParadao
          id={aberto}
          sessao={sessao}
          configura={configura}
          aoFechar={() => setAberto(null)}
          aoMudar={() => void carregar()}
        />
      )}
      {incluir && (
        <IncluirProduto
          sessao={sessao}
          aoFechar={() => setIncluir(false)}
          aoIncluir={(id) => {
            setIncluir(false);
            void carregar();
            setAberto(id);
          }}
        />
      )}
    </div>
  );
}

/** 'R$ 9.740' — o KPI mostra o inteiro; a lista mostra o valor inteiro também, como no design. */
function reaisInteiro(v: string): string {
  const [i = '0'] = v.split('.');
  return `R$ ${i.replace(/\B(?=(\d{3})+(?!\d))/g, '.')}`;
}

function Tags({ i }: { i: ItemDoParadao }) {
  const semEstoque = i.estoque === null || Number(i.estoque) <= 0;
  const t = textoDaVenda(i.venda);
  return (
    <div className="pr-tags">
      {i.tambemPreVencido && <Selo texto="Pré-vencido" tom="aviso" />}
      {semEstoque && <Selo texto="Sem estoque" tom="neutro" />}
      {t.rotulo && i.venda.estado === 'nunca_vendido' && (
        <Selo texto="Nunca vendido" tom="neutro" />
      )}
      {t.rotulo && i.venda.estado === 'indisponivel' && (
        <span className="pv-selo pv-selo-neutro">
          <Icone nome="file" tamanho={11} traco={1.8} />
          Última venda indisponível
        </span>
      )}
    </div>
  );
}

function Linha({
  i,
  aoAbrir,
  comLancados,
}: {
  i: ItemDoParadao;
  aoAbrir: () => void;
  comLancados: boolean;
}) {
  const veCusto = useVeCusto();
  const t = textoDaVenda(i.venda);
  const dias = i.venda.estado === 'sem_venda_ha' ? i.venda.dias : null;
  const semEstoque = i.estoque === null || Number(i.estoque) <= 0;
  return (
    <tr className={semEstoque ? 'pv-linha-inativa' : undefined}>
      <td>
        <div className="ac-pessoa-nome">{i.descricao}</div>
        <div className="pd-codigos">{[i.codigo, i.fabricante].filter(Boolean).join(' · ')}</div>
      </td>
      <td className={i.venda.estado === 'sem_venda_ha' ? 'ac-num' : 'ac-cinza'}>{t.data}</td>
      <td className={`ac-direita pv-dias${dias !== null && dias >= 90 ? ' pv-dias-aviso' : ''}`}>
        {dias === null ? <span className="pd-traco">—</span> : dias}
      </td>
      <td className="ac-direita ac-num ac-col-larga">{i.vendas30 ?? '—'}</td>
      <td className="ac-direita ac-num ac-col-larga">{i.vendas90 ?? '—'}</td>
      <td className="ac-direita ac-num">{i.estoque ?? '—'}</td>
      {comLancados && (
        <td className="ac-direita ac-num">
          {i.lancadosNoPeriodo ? <strong>{i.lancadosNoPeriodo}</strong> : '0'}
        </td>
      )}
      {veCusto && (
        <td className="ac-direita ac-num">
          {semEstoque ? '—' : i.custoParado ? reaisInteiro(i.custoParado) : 'indisponível'}
        </td>
      )}
      <td className="ac-mono ac-col-larga">{i.curva ?? '—'}</td>
      <td>
        <Tags i={i} />
      </td>
      <td className="ac-direita">
        <button type="button" className="ac-link" onClick={aoAbrir}>
          {semEstoque ? 'Ver' : 'Ver produto'}
        </button>
      </td>
    </tr>
  );
}

function CardDoParadao({ i, aoAbrir }: { i: ItemDoParadao; aoAbrir: () => void }) {
  const dias =
    i.venda.estado === 'sem_venda_ha'
      ? `${String(i.venda.dias)} dias`
      : textoDaVenda(i.venda).rotulo;
  return (
    <button type="button" className="ac-card" onClick={aoAbrir}>
      <div className="ac-card-linha">
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="ac-card-nome">{i.descricao}</div>
          <div className="ac-card-sub">{[i.codigo, i.fabricante].filter(Boolean).join(' · ')}</div>
        </div>
        <span className="pr-card-dias">{dias}</span>
      </div>
      <div className="pd-card-faixas">
        <div>
          <div className="pd-card-faixa-rotulo">ESTOQUE</div>
          <div className="pd-card-faixa-valor">{i.estoque ?? '—'}</div>
        </div>
        {i.lancadosNoPeriodo !== undefined && (
          <div>
            <div className="pd-card-faixa-rotulo">LANÇADOS</div>
            <div className="pd-card-faixa-valor">{i.lancadosNoPeriodo}</div>
          </div>
        )}
        <div>
          <div className="pd-card-faixa-rotulo">CURVA</div>
          <div className="pd-card-faixa-valor">{i.curva ?? '—'}</div>
        </div>
      </div>
      {i.tambemPreVencido && (
        <div className="pd-card-texto">
          Este produto está em duas categorias — a escolha da premiação acontece no registro da
          venda.
        </div>
      )}
    </button>
  );
}

function Kpi({
  rotulo,
  valor,
  sub,
  tom,
}: {
  rotulo: string;
  valor: number | undefined;
  sub: string;
  tom?: 'aviso' | 'neutro';
}) {
  return (
    <div className="ac-kpi">
      <div className="ac-kpi-rotulo">{rotulo}</div>
      <div
        className={`ac-kpi-valor${tom === 'aviso' && valor ? ' pr-kpi-aviso' : ''}${tom === 'neutro' ? ' pd-kpi-neutro' : ''}`}
      >
        {valor ?? '—'}
      </div>
      <div className="pd-kpi-sub">{sub}</div>
    </div>
  );
}

/* ================================================================== */

const MESES = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];

function DrawerDoParadao({
  id,
  sessao,
  configura,
  aoFechar,
  aoMudar,
}: {
  id: string;
  sessao: Sessao;
  configura: boolean;
  aoFechar: () => void;
  aoMudar: () => void;
}) {
  const veCusto = useVeCusto();
  const ocultos = useValoresOcultos();
  const [d, setD] = useState<DetalheDoParadao | null>(null);
  const [falha, setFalha] = useState<string | null>(null);
  const [sair, setSair] = useState(false);
  const [motivo, setMotivo] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const [tirando, setTirando] = useState(false);
  useEscape(aoFechar, true);

  useEffect(() => {
    void lerDetalheDoParadao(id).then((r) =>
      r.ok ? setD(r.dados) : setFalha(mensagemDeFalha(r.falha)),
    );
  }, [id]);

  async function confirmarSaida() {
    if (tirando) return;
    if (!d || !motivo.trim()) return setErro('Diga por que o produto sai do Paradão.');
    setTirando(true);
    setErro(null);
    const r = await tirarDoParadao(sessao, d.productId, d.versao, motivo.trim());
    setTirando(false);
    if (!r.ok) return setErro(mensagemDeFalha(r.falha));
    aoMudar();
    aoFechar();
  }

  const t = d ? textoDaVenda(d.venda) : null;
  const max = d ? Math.max(1, ...d.vendasPorMes.map((m) => Number(m.unidades) || 0)) : 1;
  return (
    <div className="ac-veu" onClick={aoFechar} role="presentation">
      <aside
        className="ac-drawer pv-drawer"
        role="dialog"
        aria-modal="true"
        aria-label={d?.descricao ?? 'Paradão'}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="ac-drawer-cabeca">
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="ac-drawer-nome">{d?.descricao ?? 'Carregando…'}</div>
            {d && (
              <div className="pd-drawer-codigos">
                {[d.codigo, d.ean ? `EAN ${d.ean}` : null, d.fabricante, d.grupo]
                  .filter(Boolean)
                  .join(' · ')}
              </div>
            )}
          </div>
          {d && (
            <div className="pv-selos-cabeca">
              <span className="pv-selo pv-selo-neutro">PARADÃO</span>
              {d.tambemPreVencido && <span className="pv-selo pv-selo-aviso">PRÉ-VENCIDO</span>}
            </div>
          )}
          <button type="button" className="ac-fechar" onClick={aoFechar} aria-label="Fechar">
            <Icone nome="x" tamanho={16} traco={1.8} />
          </button>
        </div>
        {falha && (
          <div className="ac-secao">
            <ErroDaLista
              titulo="Não foi possível carregar o produto"
              texto={falha}
              aoTentar={aoFechar}
            />
          </div>
        )}
        {d && t && (
          <>
            {d.tambemPreVencido && (
              <div className="ac-secao pr-duas-tags">
                As duas tags convivem no mesmo produto. Na hora da venda, a escolha da categoria de
                premiação segue o fluxo do registro de venda.
              </div>
            )}
            <div className="ac-secao pv-numeros">
              <div className="pv-numero">
                <div className="pd-dado-rotulo">SEM VENDA HÁ</div>
                <div className="pv-numero-valor">
                  {d.venda.estado === 'sem_venda_ha' ? `${String(d.venda.dias)} dias` : t.rotulo}
                </div>
                <div className="pv-numero-sub">
                  {d.venda.estado === 'sem_venda_ha' ? `última: ${t.data}` : 'base de vendas'}
                </div>
              </div>
              <div className="pv-numero">
                <div className="pd-dado-rotulo">ESTOQUE</div>
                <div className="pv-numero-valor">{d.estoque ?? '—'}</div>
                <div className="pv-numero-sub">
                  {d.lotePreVencidoMaisProximo
                    ? `lote pré-vencido · vence ${d.lotePreVencidoMaisProximo.slice(8, 10)}/${d.lotePreVencidoMaisProximo.slice(5, 7)}`
                    : 'do relatório'}
                </div>
              </div>
              {veCusto && (
                <div className="pv-numero">
                  <div className="pd-dado-rotulo">CUSTO PARADO</div>
                  <div className="pv-numero-valor">
                    {d.custoParado ? reaisInteiro(d.custoParado) : 'indisponível'}
                  </div>
                  <div className="pv-numero-sub">custo do servidor</div>
                </div>
              )}
              <div className="pv-numero">
                <div className="pd-dado-rotulo">CURVA</div>
                <div className="pv-numero-valor">{d.curva ?? '—'}</div>
                <div className="pv-numero-sub">classificação da fonte</div>
              </div>
            </div>
            <div className="ac-secao">
              <div className="ac-secao-cabeca">
                <div className="pd-opcao-titulo">Histórico de vendas</div>
                <span className="ac-espaco" />
                <span className="ac-nota" style={{ fontSize: 11 }}>
                  base de vendas · não o campo de última saída do cadastro
                </span>
              </div>
              <div
                className="pr-barras"
                role="img"
                aria-label="Unidades vendidas por mês nos últimos 8 meses"
              >
                {d.vendasPorMes.map((m) => {
                  const n = Number(m.unidades) || 0;
                  const [, mm = '1'] = m.mes.split('-');
                  return (
                    <div key={m.mes} className="pr-barra">
                      <span className="pr-barra-n">{m.unidades === '—' ? '—' : m.unidades}</span>
                      <span className="pr-barra-coluna">
                        <span
                          style={{
                            height: `${String(n === 0 ? 2 : Math.max(8, (n / max) * 100))}%`,
                          }}
                          className={n === 0 ? 'pr-zero' : undefined}
                        />
                      </span>
                      <span className="pr-barra-mes">{MESES[Number(mm) - 1]}</span>
                    </div>
                  );
                })}
              </div>
              <div className="pd-pe">
                {d.fonte.temBase
                  ? 'O motivo pode ser sazonalidade, preço, exposição ou demanda — a tela mostra o fato, não escolhe a causa.'
                  : 'Sem vendas importadas na base: o histórico fica indisponível.'}
              </div>
            </div>
            <div className="ac-secao">
              <div className="pd-opcao-titulo">Ação comercial</div>
              <div className="ac-subtitulo" style={{ marginTop: 3 }}>
                Estar no Paradão não cria promoção automaticamente.
                {!ocultos && <> Premiação na base: {reais(d.premioBase)}</>}
                {d.venderPor
                  ? `${ocultos ? ' Vender por' : ' · vender por'} ${reais(d.venderPor)}`
                  : ocultos
                    ? ' Sem base cadastrada.'
                    : ' · sem base cadastrada paga R$ 0,00'}
              </div>
              <div className="prd-acoes">
                <a className="ac-botao" href="#/campanhas">
                  Criar campanha
                </a>
                <a className="ac-botao" href="#/produtos">
                  Revisar preço
                </a>
                <a className="ac-botao" href="#/pre-vencidos">
                  Ver lotes
                </a>
              </div>
              <div className="pd-pe">
                Nenhuma ação altera preço sozinha: todas abrem o fluxo existente com confirmação e
                autor.
              </div>
            </div>
            <div className="ac-secao" style={{ borderBottom: 0 }}>
              <div className="ac-rotulo-mono">HISTÓRICO NO PARADÃO</div>
              {d.eventos.length === 0 && (
                <div className="ac-pe-modal">
                  Veio da migração ou da planilha, sem evento registrado aqui.
                </div>
              )}
              {d.eventos.map((e, k) => (
                <div key={k} className="ac-historico">
                  <span className="ac-historico-hora">{dataHoraCurta(e.criadoEm)}</span>
                  <div style={{ flex: 1 }}>
                    <b>{e.ator ?? 'Sistema'}</b>{' '}
                    {e.tipo === 'removido'
                      ? 'tirou do Paradão'
                      : e.tipo === 'reincluido'
                        ? 'incluiu de novo'
                        : 'incluiu no Paradão'}
                    {e.motivo && <div className="pv-motivo">Motivo: {e.motivo}</div>}
                  </div>
                </div>
              ))}
              {configura && !sair && (
                <button
                  type="button"
                  className="ac-botao ac-botao-perigo"
                  style={{ marginTop: 10 }}
                  onClick={() => setSair(true)}
                >
                  Tirar do Paradão
                </button>
              )}
              {sair && (
                <div className="pv-existente">
                  <div className="pv-existente-titulo">Tirar do Paradão</div>
                  <textarea
                    className="ac-texto-area"
                    style={{ marginTop: 8 }}
                    value={motivo}
                    onChange={(e) => setMotivo(e.target.value)}
                    placeholder="Ex.: voltou a vender"
                  />
                  {erro && <div className="mc-erro">{erro}</div>}
                  <div className="pv-existente-acoes">
                    <button
                      type="button"
                      className="ac-botao ac-botao-perigo"
                      disabled={tirando}
                      onClick={() => void confirmarSaida()}
                    >
                      {tirando ? 'Tirando…' : 'Confirmar'}
                    </button>
                    <button
                      type="button"
                      className="ac-botao"
                      disabled={tirando}
                      onClick={() => setSair(false)}
                    >
                      Cancelar
                    </button>
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </aside>
    </div>
  );
}

function IncluirProduto({
  sessao,
  aoFechar,
  aoIncluir,
}: {
  sessao: Sessao;
  aoFechar: () => void;
  aoIncluir: (id: string) => void;
}) {
  const [busca, setBusca] = useState('');
  const [itens, setItens] = useState<readonly ProdutoComercial[]>([]);
  const [erro, setErro] = useState<string | null>(null);
  // Clicar no resultado só ESCOLHE; incluir pede o segundo clique (entra na lista na hora).
  const [escolhido, setEscolhido] = useState<ProdutoComercial | null>(null);
  const [incluindo, setIncluindo] = useState(false);
  useEscape(aoFechar, !incluindo);

  useEffect(() => {
    if (busca.trim().length < 2) return setItens([]);
    const t = window.setTimeout(() => {
      void listarProdutos({ filtro: 'todos', busca, grupo: '', pagina: 1 }).then((r) => {
        if (r.ok) setItens(r.dados.itens.slice(0, 8));
      });
    }, 300);
    return () => window.clearTimeout(t);
  }, [busca]);

  async function incluir(id: string) {
    if (incluindo) return;
    setIncluindo(true);
    setErro(null);
    const r = await incluirNoParadao(sessao, id, null);
    setIncluindo(false);
    if (!r.ok) return setErro(mensagemDeFalha(r.falha));
    aoIncluir(id);
  }

  return (
    <div
      className="ac-veu ac-veu-centro"
      onClick={() => {
        if (!incluindo) aoFechar();
      }}
      role="presentation"
    >
      <div
        className="ac-modal"
        role="dialog"
        aria-modal="true"
        aria-label="Incluir produto no Paradão"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="ac-modal-cabeca">
          <div className="ac-modal-titulo">Incluir produto no Paradão</div>
          <div className="ac-modal-sub">Do catálogo existente — o Paradão não cria produto.</div>
        </div>
        <div className="ac-modal-corpo">
          <label className="ac-busca" style={{ width: '100%', height: 36 }}>
            <Icone nome="search" tamanho={14} traco={1.6} />
            <input
              autoFocus
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              placeholder="Produto, EAN ou código"
            />
          </label>
          <div className="pr-resultados">
            {itens.map((p) => (
              <button
                key={p.id}
                type="button"
                className="pr-resultado"
                aria-pressed={escolhido?.id === p.id}
                disabled={incluindo}
                onClick={() => {
                  setErro(null);
                  setEscolhido(p);
                }}
              >
                <span className="ac-pessoa-nome">{p.descricao}</span>
                <span className="pd-codigos">
                  {[p.codigo, p.fabricante].filter(Boolean).join(' · ')}
                </span>
              </button>
            ))}
            {busca.trim().length >= 2 && itens.length === 0 && (
              <div className="ac-pe-modal">Nenhum produto do catálogo com esse nome.</div>
            )}
          </div>
          {escolhido && (
            <div className="pv-existente" role="status">
              <div className="pv-existente-titulo">Incluir {escolhido.descricao} no Paradão?</div>
              <div className="ac-pe-modal">
                Entra na lista do Paradão agora, com o seu nome no histórico do produto.
              </div>
              <div className="pv-existente-acoes">
                <button
                  type="button"
                  className="ac-botao ac-botao-primario"
                  disabled={incluindo}
                  onClick={() => void incluir(escolhido.id)}
                >
                  {incluindo ? 'Incluindo…' : 'Incluir'}
                </button>
                <button
                  type="button"
                  className="ac-botao"
                  disabled={incluindo}
                  onClick={() => setEscolhido(null)}
                >
                  Cancelar
                </button>
              </div>
            </div>
          )}
          {erro && <div className="mc-erro">{erro}</div>}
        </div>
      </div>
    </div>
  );
}
